import { LaunchLink } from '@components/links/LaunchLink'
import { UserChip } from '@components/user/userChip'
import { User, Submission } from '@global/types'
import { useSsoEnabled } from '@global/hooks/queries/useAuthHintQuery'
import { RowPropertyPopover } from './rowPropertyPopover'
import { t } from '@lingui/core/macro'

type ValidRowPropertyType = User | Submission | string

interface Props {
  cellData: { value: ValidRowPropertyType }
  open: boolean
  anchorEl: HTMLElement | null
}

export const RowPropertyValue = ({ cellData, anchorEl, open }: Props) => {
  const { value } = cellData

  const ssoEnabled = useSsoEnabled()

  if (!value) {
    return (
      <>
        {' '}
        -
        <RowPropertyPopover
          anchorEl={anchorEl}
          open={open}
          warnings={[
            {
              defaultErrorMsg: t`Unable to load data. Please try submitting again.`,
            },
          ]}
        />
      </>
    )
  }

  if (determineIfIsSubmission(value)) {
    return (
      <div style={{ pointerEvents: 'auto' }}>
        <LaunchLink
          textStyle={{
            maxWidth: 'calc(100% - 21px)',
            textOverflow: 'ellipsis',
            display: 'inline-block',
            overflow: 'hidden',
            verticalAlign: 'middle',
          }}
          // check for sso flag is set on the link below to give 3rd party users a way to open a new tab without entering an sso login loop
          href={
            !ssoEnabled
              ? `/codes/details/${String(value.dataset_id)}/data?login=system`
              : `/codes/details/${String(value.dataset_id)}/data`
          }
          text={value.submission_name ?? '[Untitled]'}
        />
      </div>
    )
  }

  if (determineIfIsUser(value)) {
    return <UserChip user={value} size="small" />
  }

  return <>{value}</>
}

function determineIfIsSubmission(
  toBeDetermined: ValidRowPropertyType,
): toBeDetermined is Submission {
  return (
    typeof toBeDetermined === 'object' && 'id' in toBeDetermined && 'dataset_id' in toBeDetermined
  )
}

function determineIfIsUser(toBeDetermined: ValidRowPropertyType): toBeDetermined is User {
  return typeof toBeDetermined === 'object' && 'id' in toBeDetermined && 'email' in toBeDetermined
}
